import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '../../../components/inputs/LoadingButton';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import ConsolidatedForm from '../../../features/insurance/forms/consolidatedReport/ConsolidatedForm';
import { InsuranceConsolidatedForm } from '../../../Interfaces/Insurance/Forms';
import { downloadFile } from '@/src/utils/fileUtil';
import { postConsolidatedReport } from '@/src/app/services/Insurance/Policy';
import { getBooleanStatus } from '@/src/utils/formatUtils';

const PolicyConsolidated = () => {
  const formMethods = useForm<InsuranceConsolidatedForm>();
  const { handleSubmit } = formMethods;
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (data: InsuranceConsolidatedForm) => {
      return postConsolidatedReport(
        data.companies,
        data.periodStart ?? undefined,
        data.periodEnd ?? undefined,
        data.status ? getBooleanStatus(data.status) : undefined
      );
    },
    onSuccess: (response) => {
      downloadFile(response);
    },
    onError: (error: AxiosError) => {
      const status = error.status;
      showToast(
        'error',
        toast,
        'Policy Consolidated Report Error',
        status === 404
          ? 'No Policies found!'
          : 'An error occurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: InsuranceConsolidatedForm) => {
    mutate(data);
  };

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-4">
      <div className="text-35px font-bold text-center mb-2 relative">
        Policy Consolidated Report
      </div>
      <FormProvider {...formMethods}>
        <form className="max-w-600px w-full mx-auto flex flex-column gap-5">
          <ConsolidatedForm />
          <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
            <ConfirmationDialog
              Button={
                <LoadingButton
                  isLoading={isPending}
                  label="Generate"
                  className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                  fontSize="text-22px"
                  type="button"
                />
              }
              onConfirm={handleSubmit(onSubmit)}
              message="Are you sure you want to generate the consolidated report?"
            />
            <Button
              onClick={() => {
                navigate('/insurance/new');
              }}
              className="w-15rem bluwaiRed border-0 flex m-auto py-3"
              type="button"
            >
              <span className="mx-auto text-22px text-white">Cancel</span>
            </Button>
          </div>
        </form>
      </FormProvider>
      <ErrorToast toastRef={toast} />
    </div>
  );
};

export default PolicyConsolidated;
